import {
  Box,
  ButtonIcon,
  Heading,
  IconArrow,
  IconCompose,
  IconDelete,
  IconOverflow,
  IconShare,
  Inline,
  PageBlock,
  Spread,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProvider,
  Text,
} from 'braid-design-system';

import {
  SeekPassIdVerifiedIndicator,
  SeekPassLegend,
} from './SeekPassIdVerifiedIndicator';

import {
  Annotation,
  AnnotationContainer,
} from '#src/components/Annotation/Annotation.tsx';
import { ThemedExample } from '#src/components/ThemedExample/ThemedExample.tsx';

export const ShowVerificationResultExample = () => (
  <ThemedExample>
    <Stack space="large">
      <Text size="large" weight="strong">
        Barista (Part-time)
      </Text>
      <TabsProvider onChange={() => undefined} selectedItem="applications">
        <Stack space="medium">
          <Tabs label="Applications">
            <Tab item="applications">Applications</Tab>
            {/* Nonexistent second tab for demonstration purposes */}
            <Tab item="applications">Job details</Tab>
          </Tabs>
          <TabPanels>
            <TabPanel item="applications">
              <Table label="Applications">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>
                      <Text>Name</Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text>Source</Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text>Status</Text>
                    </TableHeaderCell>
                    <TableHeaderCell width="content" align="right">
                      <Text>Actions</Text>
                    </TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    {
                      name: 'John Smith',
                      source: 'SEEK',
                      seekProfile: true,
                      idCheck: false,
                      status: 'New',
                    },
                    {
                      name: 'Sarah Johnson',
                      source: 'Careers website',
                      seekProfile: true,
                      idCheck: true,
                      status: 'Shortlisted',
                    },
                    {
                      name: 'Michael Brown',
                      source: 'Other',
                      seekProfile: false,
                      idCheck: false,
                      status: 'Offered',
                    },
                    {
                      name: 'Emma Wilson',
                      source: 'SEEK',
                      seekProfile: true,
                      idCheck: true,
                      status: 'New',
                    },
                    {
                      name: 'David Lee',
                      source: 'Other',
                      seekProfile: true,
                      idCheck: true,
                      status: 'Shortlisted',
                    },
                    {
                      name: 'Rachel Green',
                      source: 'SEEK',
                      seekProfile: true,
                      idCheck: true,
                      status: 'New',
                    },
                    {
                      name: 'James Taylor',
                      source: 'Careers website',
                      seekProfile: true,
                      idCheck: false,
                      status: 'Shortlisted',
                    },
                    {
                      name: 'Lisa Anderson',
                      source: 'Other',
                      seekProfile: false,
                      idCheck: false,
                      status: 'Offered',
                    },
                    {
                      name: 'Peter Wright',
                      source: 'SEEK',
                      seekProfile: true,
                      idCheck: true,
                      status: 'New',
                    },
                    {
                      name: 'Amy Chen',
                      source: 'Careers website',
                      seekProfile: false,
                      idCheck: true,
                      status: 'Shortlisted',
                    },
                  ].map((row) => (
                    <TableRow key={row.name}>
                      <TableCell>
                        <Inline space="xxsmall" alignY="center">
                          <Text>{row.name}</Text>
                          {row.idCheck && (
                            <SeekPassIdVerifiedIndicator
                              idSuffix={`demo-${row.name}`}
                              name={row.name}
                            />
                          )}
                        </Inline>
                      </TableCell>
                      <TableCell>
                        <Text>{row.source}</Text>
                      </TableCell>
                      <TableCell>
                        <Text>{row.status}</Text>
                      </TableCell>
                      <TableCell width="content" align="right">
                        <ButtonIcon
                          icon={<IconOverflow />}
                          label="Actions"
                          variant="transparent"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabPanel>
          </TabPanels>
        </Stack>
      </TabsProvider>

      <Stack space="small">
        <Text tone="secondary">Legend:</Text>

        <SeekPassLegend />
      </Stack>
    </Stack>
  </ThemedExample>
);

export const VerifyWithSeekPassButton = () => (
  <Box
    component="img"
    src="https://integration.seek.com/assets/verify-with-seek-pass/button-images/en/standard-accent.svg"
    alt="Verify with SEEK Pass"
  />
);

export const EmailExample = () => (
  <ThemedExample>
    <Stack space="large" align="center">
      <Box
        paddingY="large"
        maxWidth="small"
        background="surface"
        borderRadius="standard"
        boxShadow="medium"
        style={{ textAlign: 'left' }}
      >
        <Stack space="large">
          <PageBlock width="medium">
            <Stack space="large">
              <Spread space="medium" alignY="center">
                <ButtonIcon
                  icon={<IconArrow direction="left" />}
                  label="Back"
                  variant="transparent"
                  size="large"
                />

                <Inline space="small">
                  <ButtonIcon
                    icon={<IconCompose />}
                    label="Reply"
                    variant="transparent"
                    size="large"
                  />
                  <ButtonIcon
                    icon={<IconShare />}
                    label="Forward"
                    variant="transparent"
                    size="large"
                  />
                  <ButtonIcon
                    icon={<IconDelete />}
                    label="Delete"
                    variant="transparent"
                    size="large"
                  />
                </Inline>
              </Spread>

              <Stack space="medium">
                <Stack space="small">
                  <Heading level="4">
                    Verify your identity for your application to {'{'}
                    COMPANY_NAME
                    {'}'}
                  </Heading>
                  <Text tone="secondary" size="small">
                    From: careers@hirer.example
                  </Text>
                  <Text tone="secondary" size="small">
                    To: candidate@mail.example
                  </Text>
                </Stack>

                <Stack space="medium">
                  <Text>
                    Hi{' '}
                    <b>
                      {'{'}CANDIDATE_NAME{'}'}
                    </b>
                    ,
                  </Text>

                  <Text>
                    Thank you for applying for{' '}
                    <b>
                      {'{'}ROLE{'}'}
                    </b>{' '}
                    at{' '}
                    <b>
                      {'{'}COMPANY_NAME{'}'}
                    </b>{' '}
                    . We&apos;re inviting you to verify your identity through
                    SEEK Pass.
                  </Text>

                  <Text>
                    It only takes a few minutes and adds a verified badge to
                    your profile, helping us review it with greater confidence.
                  </Text>

                  <Text>
                    While this step is optional, we encourage candidates to
                    complete it as part of the application process.
                  </Text>

                  <Stack space="medium" align="center">
                    <Box maxWidth="medium">
                      <VerifyWithSeekPassButton />
                    </Box>
                  </Stack>

                  <Stack space="medium">
                    <Text>We look forward to reviewing your application.</Text>

                    <Text>
                      Best regards,
                      <br />
                      The{' '}
                      <b>
                        {'{'}COMPANY_NAME{'}'}
                      </b>{' '}
                      Hiring Team
                    </Text>

                    <AnnotationContainer>
                      <Annotation>
                        <strong>Required copy</strong> for additional context on
                        SEEK Pass
                      </Annotation>

                      <Stack space="medium">
                        <Text>
                          <strong>What is SEEK Pass?</strong>
                        </Text>
                        <Text>
                          What is SEEK Pass? SEEK Pass is a secure platform used
                          to verify work-related credentials. Hirers use it to
                          verify information in candidate applications.
                        </Text>
                      </Stack>
                    </AnnotationContainer>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </PageBlock>
        </Stack>
      </Box>
    </Stack>
  </ThemedExample>
);
